import React, { useCallback, useState } from 'react';

import PagePanel from 'src/components/PagePanel';
import PageHeader from 'src/components/PageHeader';
import { Button } from 'src/components/Button';
import { Table } from 'src/components/TableNew';
import NoResult from 'src/components/NoResult/NoResult';
import { PermissionGuard } from 'src/components/PermissionGuard';
import { FilterBar, type FilterDef } from 'src/components/FilterBar';
import { useFilterQueryEffect } from 'src/hooks/useFilterQueryEffect';
import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import { PERMISSIONS } from 'src/utils/permissions';
import { convertDate } from 'src/components/Table/helpers';
import {
  NotificationCampaign,
  campaignErrorMessage,
  describeAudience,
  fetchCampaigns,
} from 'src/api/adminNotifications';

import NewCampaignModal from './NewCampaignModal';
import CampaignDetailModal from './CampaignDetailModal';
import { Muted, StatusBadge } from './styled';

const CAMPAIGN_FILTERS: FilterDef[] = [
  { key: 'title', label: 'Title', placeholder: 'Starts with…' },
  {
    key: 'status',
    label: 'Status',
    options: [
      { value: 'PREPARING', label: 'Preparing' },
      { value: 'QUEUED', label: 'Queued' },
      { value: 'PROCESSING', label: 'Processing' },
      { value: 'COMPLETED', label: 'Completed' },
      { value: 'CANCELED', label: 'Canceled' },
    ],
    allLabel: 'All statuses',
  },
  {
    key: 'audience_type',
    label: 'Audience',
    options: [
      { value: 'all', label: 'All users' },
      { value: 'country', label: 'By nationality' },
      { value: 'kyc', label: 'By KYC status' },
      { value: 'users', label: 'Specific users' },
    ],
    allLabel: 'Any audience',
  },
  {
    key: 'email',
    label: 'Email channel',
    options: [
      { value: 'true', label: 'With email' },
      { value: 'false', label: 'In-app only' },
    ],
    allLabel: 'Any',
  },
  { key: 'created_by', label: 'Created by', placeholder: 'Admin email…' },
  { key: 'from', label: 'From', type: 'date' },
  { key: 'to', label: 'To', type: 'date' },
];

const HEADERS = ['Title', 'Audience', 'Channels', 'Status', 'Delivered', 'Failed', 'Created by', 'Created'];

const toIso = (value: string, endOfDay: boolean) => {
  if (!value) return undefined;
  const d = new Date(`${value}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};

const Notifications = () => {
  const dispatch = useDispatch();
  const [campaigns, setCampaigns] = useState<NotificationCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [composing, setComposing] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = useCallback(
    async (params: { page: number; filters: Record<string, string> }) => {
      setLoading(true);
      try {
        const { from, to, ...rest } = params.filters;
        const res = await fetchCampaigns({
          page: params.page,
          limit: 10,
          ...rest,
          from: toIso(from, false),
          to: toIso(to, true),
        });
        setCampaigns(res.campaigns);
        setTotalCount(res.meta.total_count || 0);
        setTotalPages(Math.max(1, res.meta.page_count || 1));
      } catch (e: any) {
        dispatch(setAppAlert({ message: campaignErrorMessage(e, 'Failed to load campaigns'), isSuccess: false }));
        setCampaigns([]);
      } finally {
        setLoading(false);
      }
    },
    [dispatch],
  );

  const { applied: appliedFilters, refresh: refreshQuery } = useFilterQueryEffect({ filters, currentPage, setCurrentPage, query: load });
  const reload = useCallback(() => load({ page: currentPage, filters: appliedFilters }), [appliedFilters, currentPage, load]);

  const rows = campaigns.map((c) => ({
    ...c,
    audience_display: describeAudience(c.audience),
    channels_display: c.channels.email ? 'In-app + email' : 'In-app',
    delivered_display: `${c.counts.delivered} / ${c.counts.targeted}`,
    failed_display: c.counts.failed,
    created_by_display: c.created_by_email || '—',
    created_display: convertDate(c.created_at).date,
  }));

  return (
    <PagePanel>
      <PageHeader
        title={`Notifications (${totalCount})`}
        hideSearch
        headerActions={
          <PermissionGuard permission={PERMISSIONS.USER_WRITE}>
            <Button text="New campaign" className="black" onClick={() => setComposing(true)} />
          </PermissionGuard>
        }
      >
        <FilterBar
          filters={CAMPAIGN_FILTERS}
          values={filters}
          onChange={(key, value) => setFilters((prev) => ({ ...prev, [key]: value }))}
          onClear={() => setFilters({})}
          onRefresh={refreshQuery}
        />
      </PageHeader>

      {rows.length > 0 || loading ? (
        <Table
          loading={loading}
          headers={HEADERS}
          items={rows}
          columns="1.6fr 1.3fr 0.8fr 0.8fr 0.8fr 0.5fr 1.1fr 0.8fr"
          fieldsToShow={[
            'title',
            'audience_display',
            'channels_display',
            'status',
            'delivered_display',
            'failed_display',
            'created_by_display',
            'created_display',
          ]}
          totalPages={totalPages}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          sortableFields={[]}
          disableRowDetails
          onRowClick={(item) => setSelectedId(String(item.id))}
          renderCell={(item, field) => {
            if (field === 'status') return <StatusBadge $status={String(item.status)}>{String(item.status)}</StatusBadge>;
            if (field === 'failed_display' && !item.failed_display) return <Muted>0</Muted>;
            return String(item[field] ?? '—');
          }}
        />
      ) : (
        <NoResult />
      )}

      {composing ? (
        <NewCampaignModal
          onClose={() => setComposing(false)}
          onCreated={() => {
            setComposing(false);
            reload();
          }}
        />
      ) : null}
      {selectedId ? (
        <CampaignDetailModal campaignId={selectedId} onClose={() => setSelectedId(null)} onChanged={reload} />
      ) : null}
    </PagePanel>
  );
};

export default Notifications;
